/**
 * Cleanup icon caches when the cache version changes.
 *
 * When the data-icon-cache-version attribute changes (e.g., when deploying a new version
 * or switching branches), this script deletes all existing caches to prevent stale icons
 * from being served. The icon component will recreate the cache as needed.
 */

const STORAGE_KEY = 'lastIconCacheIdentifier';

async function cleanupIconCache(): Promise<void> {
  // Check if Cache Storage API is available
  if (!('caches' in window)) {
    return;
  }

  const currentVersion = document.body.dataset.iconCacheVersion;

  // If no version is set, skip cleanup
  if (!currentVersion) {
    return;
  }

  const lastVersion = localStorage.getItem(STORAGE_KEY);

  // If version hasn't changed, no cleanup needed
  if (lastVersion === currentVersion) {
    return;
  }

  // If this is the first visit (no previous version), just store the version
  if (!lastVersion) {
    localStorage.setItem(STORAGE_KEY, currentVersion);
    return;
  }

  try {
    // Get all cache names
    const cacheNames = await caches.keys();

    // Delete all caches
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));

    // Update stored version
    localStorage.setItem(STORAGE_KEY, currentVersion);
  } catch (error) {
    console.error('[Icon Cache] Failed to cleanup caches:', error);
  }
}

if (document.readyState === 'complete') {
  cleanupIconCache();
} else {
  window.addEventListener('load', () => cleanupIconCache(), { once: true });
}
